/**
 * AuditDetailPage — Full detail view for a single LPA audit.
 *
 * Features:
 * - Breadcrumb navigation back to audit list
 * - Header card with audit name, status badge, owner, location, description
 * - Edit button → opens AuditFormDialog
 * - Status transition buttons (draft→active, active→archived)
 * - Delete button for drafts with confirmation
 * - Schedules section with full CRUD and question assignment (Phase 2)
 *
 * Routed at /lpa/audits/:auditId
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/core/LanguageContext';
import { useAuditById, useUpdateAudit, useDeleteAudit } from '../hooks/useAudits';
import type { LpaAuditWithDetails, LpaAuditStatus } from '../types';
import AuditFormDialog from './AuditFormDialog';
import ScheduleListSection from './ScheduleListSection';
import TimelineSection from './TimelineSection';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Separator } from '@/shared/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Play,
  Archive,
  MapPin,
  CalendarClock,
  User,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  LpaAuditStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline'; className: string }
> = {
  draft: {
    label: 'Draft',
    variant: 'outline',
    className: 'border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-400',
  },
  active: {
    label: 'Active',
    variant: 'default',
    className: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-600',
  },
  archived: {
    label: 'Archived',
    variant: 'secondary',
    className: 'text-muted-foreground',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({
  status,
  t,
  size = 'default',
}: {
  status: LpaAuditStatus;
  t: (key: string, fallback: string) => string;
  size?: 'default' | 'large';
}) {
  const config = STATUS_CONFIG[status];
  const label = t(`mod_lpa.audits.status.${status}`, config.label);

  return (
    <Badge
      variant={config.variant}
      className={`${config.className} ${size === 'large' ? 'text-sm px-3 py-1' : ''}`}
    >
      {label}
    </Badge>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-5 w-64" />
      <Skeleton className="h-[200px] w-full rounded-lg" />
      <Skeleton className="h-[150px] w-full rounded-lg" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Not found state
// ---------------------------------------------------------------------------

function NotFoundState({ t }: { t: (key: string, fallback: string) => string }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">
        {t('mod_lpa.audits.detail.notFound', 'Audit not found')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">
        {t(
          'mod_lpa.audits.detail.notFoundHint',
          'This audit may have been deleted or you may not have access.',
        )}
      </p>
      <Button variant="outline" onClick={() => navigate('/lpa?tab=audits')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('mod_lpa.audits.detail.backToList', 'Back to Audits')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AuditDetailPage() {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { data: audit, isLoading } = useAuditById(auditId);
  const updateAudit = useUpdateAudit();
  const deleteAudit = useDeleteAudit();

  // UI state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Handlers
  const handleEditSubmit = (data: {
    name: string;
    description: string;
    owner_id: string;
    area_id: string | null;
    line_id: string | null;
    station_id: string | null;
  }) => {
    if (!auditId) return;
    updateAudit.mutate(
      {
        auditId,
        input: {
          name: data.name,
          description: data.description || null,
          owner_id: data.owner_id,
          area_id: data.area_id,
          line_id: data.line_id,
          station_id: data.station_id,
        },
      },
      { onSuccess: () => setEditDialogOpen(false) },
    );
  };

  const handleStatusTransition = (newStatus: LpaAuditStatus) => {
    if (!auditId) return;
    updateAudit.mutate({ auditId, input: { status: newStatus } });
  };

  const handleDelete = () => {
    if (!auditId) return;
    deleteAudit.mutate(auditId, {
      onSuccess: () => navigate('/lpa?tab=audits'),
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Skeleton className="h-4 w-20" />
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <DetailSkeleton />
      </div>
    );
  }

  // Not found
  if (!audit) {
    return <NotFoundState t={t} />;
  }

  const isDraft = audit.status === 'draft';
  const isActive = audit.status === 'active';
  const isArchived = audit.status === 'archived';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              href="/lpa?tab=audits"
              onClick={(e) => {
                e.preventDefault();
                navigate('/lpa?tab=audits');
              }}
            >
              {t('mod_lpa.audits.detail.breadcrumbAudits', 'Audits')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{audit.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-3">
                <CardTitle className="text-xl">{audit.name}</CardTitle>
                <StatusBadge status={audit.status} t={t} size="large" />
              </div>
              {audit.description && (
                <CardDescription className="text-sm">
                  {audit.description}
                </CardDescription>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {!isArchived && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  {t('mod_lpa.audits.detail.edit', 'Edit')}
                </Button>
              )}

              {isDraft && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        onClick={() => handleStatusTransition('active')}
                        disabled={updateAudit.isPending}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {updateAudit.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4 mr-1.5" />
                        )}
                        {t('mod_lpa.audits.detail.activate', 'Activate')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                      {t(
                        'mod_lpa.audits.detail.activateHint',
                        'Activating this audit signals that its schedules should start generating instances. Make sure schedules are configured first.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {isActive && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleStatusTransition('archived')}
                        disabled={updateAudit.isPending}
                      >
                        {updateAudit.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <Archive className="h-4 w-4 mr-1.5" />
                        )}
                        {t('mod_lpa.audits.detail.archive', 'Archive')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                      {t(
                        'mod_lpa.audits.detail.archiveHint',
                        'Archiving stops future instance generation but preserves all historical data and completions.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {isDraft && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Owner */}
            <DetailRow icon={User} label={t('mod_lpa.audits.detail.ownerLabel', 'Owner')}>
              <div className="flex items-center gap-2 mt-0.5">
                <Avatar className="h-6 w-6">
                  <AvatarImage
                    src={audit.owner?.avatar_url ?? undefined}
                    alt={audit.owner?.full_name ?? ''}
                  />
                  <AvatarFallback className="text-[10px]">
                    {audit.owner
                      ? getInitials(audit.owner.full_name, audit.owner.email)
                      : '?'}
                  </AvatarFallback>
                </Avatar>
                <span>
                  {audit.owner?.full_name ?? audit.owner?.email ?? t('mod_lpa.audits.unknownOwner', 'Unknown')}
                </span>
              </div>
            </DetailRow>

            {/* Location */}
            <DetailRow icon={MapPin} label={t('mod_lpa.audits.detail.locationLabel', 'Location')}>
              {(() => {
                const parts: string[] = [];
                if (audit.area_name) parts.push(audit.area_name);
                if (audit.line_name) parts.push(audit.line_name);
                if (audit.station_name) parts.push(audit.station_name);
                return parts.length > 0
                  ? parts.join(' > ')
                  : t('mod_lpa.audits.entireFactory', 'Entire factory');
              })()}
            </DetailRow>

            {/* Schedule count */}
            <DetailRow
              icon={CalendarClock}
              label={t('mod_lpa.audits.detail.schedulesLabel', 'Schedules')}
            >
              <span>
                {audit.schedule_count === 0
                  ? t('mod_lpa.audits.detail.noSchedules', 'None configured')
                  : `${audit.schedule_count} ${t('mod_lpa.audits.detail.scheduleSuffix', 'schedule(s)')}`}
              </span>
            </DetailRow>

            {/* Created */}
            <DetailRow icon={Clock} label={t('mod_lpa.audits.detail.createdLabel', 'Created')}>
              {formatDateTime(audit.created_at)}
            </DetailRow>
          </div>
        </CardContent>
      </Card>

      {/* Schedules section — Phase 2 */}
      <ScheduleListSection auditId={audit.id} isArchived={isArchived} />

      {/* Instance timeline — Phase 3 */}
      <TimelineSection auditId={audit.id} isArchived={isArchived} />

      {/* Edit dialog */}
      <AuditFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        audit={audit}
        onSubmit={handleEditSubmit}
        isSubmitting={updateAudit.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('mod_lpa.audits.detail.deleteTitle', 'Delete audit?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'mod_lpa.audits.detail.deleteDescription',
                `This will permanently delete "${audit.name}" and all its schedules. This action cannot be undone.`,
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('mod_lpa.audits.detail.deleteCancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAudit.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('mod_lpa.audits.detail.deleteConfirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
